import type { AxiosRequestConfig } from 'axios'
import { api } from '../services/API'
import { toArray } from './toArray'

// Tamanho de página usado para varrer um endpoint paginado. Grande o suficiente
// para que a esmagadora maioria das colecções caiba num único pedido.
const SWEEP_PAGE_SIZE = 200

/**
 * Traz a colecção COMPLETA de um endpoint que o backend devolve paginado
 * (`Page<T>` do Spring — tamanho por omissão 20/50).
 *
 * Ecrãs que pesquisam/filtram/paginam no cliente, contadores e
 * selects/autocompletes precisam da lista toda; sem isto perde-se
 * silenciosamente tudo o que fique além da primeira página.
 *
 * Pede a página 0 com um tamanho grande e, se ainda houver mais, traz as
 * restantes em paralelo. Se o endpoint não for paginado (devolve logo um
 * array), esse array é devolvido tal como vem.
 *
 * @param config  Config axios adicional (ex: `params` de filtro,
 *                 `suppressForbiddenToast`). `page`/`size` são geridos aqui.
 */
export async function fetchAllPages<T>(
  endpoint: string,
  config: AxiosRequestConfig = {},
): Promise<T[]> {
  const requestPage = (page: number) =>
    api.get(endpoint, { ...config, params: { ...config.params, page, size: SWEEP_PAGE_SIZE } })

  const first = await requestPage(0)
  if (Array.isArray(first.data)) return first.data as T[]

  const totalPages: number = first.data?.totalPages ?? 1
  if (totalPages <= 1) return toArray<T>(first.data)

  const rest = await Promise.all(
    Array.from({ length: totalPages - 1 }, (_, i) => requestPage(i + 1)),
  )
  return [...toArray<T>(first.data), ...rest.flatMap((r) => toArray<T>(r.data))]
}
